import UiIcon from "../UiIcon";
import { formatPercent, formatScore, formatSigned, periodDisplayLabel, toNumber } from "./historyModel";

// Read-only record of one period for one person. Every value comes from the period's history
// payload (final snapshot for a locked period, live calculation for the active one) and existing
// records; nothing is estimated, and sections without data say so.

function StateBadge({ state, c }) {
    return <span className={`cvi-history-state is-${state}`}><i aria-hidden="true" />{c.status[state] || c.status.unknown}</span>;
}

export function ScoreTrio({ c, language, scores, changes = {}, state, emptyText }) {
    const o = c.overview;
    const delta = key => {
        const ch = changes[key];
        if (!ch) return null;
        if (ch.withheld) return <small className="cvi-history-delta is-muted">{o.withheld}</small>;
        if (ch.value == null) return ch.previous ? null : <small className="cvi-history-delta is-muted">{o.noPrevious}</small>;
        const tone = Math.abs(ch.value) < 0.05 ? "flat" : ch.value > 0 ? "up" : "down";
        return <small className={`cvi-history-delta is-${tone}`}>
            <span aria-hidden="true">{tone === "up" ? "▲" : tone === "down" ? "▼" : "■"}</span> {formatSigned(ch.value, language)} {o.vs(periodDisplayLabel(ch.previous, language))}
        </small>;
    };
    const tile = (key, label, variant) => <div className={`cvi-history-score is-${variant}`}>
        <span className="cvi-history-score-label">{label}</span>
        <span className="cvi-history-score-value">{formatScore(scores?.[key], language)}{toNumber(scores?.[key]) != null && <small>{o.outOf}</small>}</span>
        {delta(key)}
    </div>;
    const empty = ["cvi", "individual", "org"].every(key => toNumber(scores?.[key]) == null);
    return <div className={`cvi-history-trio${state === "provisional" ? " is-provisional" : ""}`}>
        {tile("individual", o.individual, "individual")}
        {tile("cvi", o.cvi, "cvi")}
        {tile("org", o.collective, "collective")}
        {empty && <p className="cvi-history-trio-empty">{emptyText || o.noScore}</p>}
    </div>;
}

function Section({ title, score, language, icon, children, wide = false }) {
    return <section className={`cvi-history-section${wide ? " is-wide" : ""}`}>
        <header>
            <h3><span className="cvi-history-section-icon" aria-hidden="true"><UiIcon name={icon} size={15} /></span>{title}</h3>
            {toNumber(score) != null && <span className="cvi-history-section-score">{formatScore(score, language)}<small>/ 100</small></span>}
        </header>
        {children}
    </section>;
}

// "80%" in English, "80 %" in French; other units keep a space.
const withUnit = (value, unit, language) => value == null || value === "" ? "—"
    : !unit ? String(value) : unit === "%" ? (language === "fr" ? `${value}\u00a0%` : `${value}%`) : `${value} ${unit}`;

const Empty = ({ children }) => <p className="cvi-history-section-empty">{children}</p>;

export default function HistoryPeriodDetail({
    c, language, period, state, data, loading, error, onRetry, onBack, onPrev, onNext, prevPeriod, nextPeriod,
    people = [], roleOptions = [], roleFilter, onRoleFilter, subjectId, onSubject, selected, changes,
}) {
    const d = c.detail;
    const statusText = value => c.statuses[String(value || "").toLowerCase()] || value || "—";
    const payload = selected?.snapshot?.calculationPayload || selected?.calculationPayload || {};
    const scores = payload.scores || selected?.scores || {};
    const breakdown = payload.breakdown || {};
    const hasBreakdown = Object.keys(breakdown).length > 0;
    const goals = selected?.goals || breakdown.individualGoals?.goals || [];
    const activities = selected?.activities || [];
    const self = selected?.selfAssessments || [];
    const monthOf = r => Number(r.month || r.question?.month) || 0;
    const routine = [...(selected?.routineAnswers || breakdown.routine?.questions || [])].sort((a, b) => monthOf(a) - monthOf(b));
    const levelText = name => (name && c.levels[name]) || name;
    const peer = breakdown.peerFeedback || selected?.peerFeedback || null;
    const org = breakdown.organizational || {};
    const formulaVersion = selected?.snapshot?.formulaVersion || payload.formulaVersion || null;
    const meta = data?.period || period || {};
    const fmtDate = value => {
        if (!value) return "";
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat(c.locale, { year: "numeric", month: "short", day: "numeric" }).format(date);
    };
    const range = meta.startDate && meta.endDate ? `${fmtDate(meta.startDate)} – ${fmtDate(meta.endDate)}` : "";
    const trioScores = { cvi: scores.cvi, individual: scores.individual, org: scores.organizational };

    return <div className="cvi-history-detail">
        <nav className="cvi-history-detail-nav" aria-label={d.back}>
            <button type="button" className="adm-btn adm-btn--sm" onClick={onBack}><UiIcon name="chevronLeft" size={14} />{d.back}</button>
            <div className="cvi-history-stepper">
                <button type="button" className="adm-btn adm-btn--sm" disabled={!prevPeriod} onClick={onPrev} aria-label={d.previous}>
                    <UiIcon name="chevronLeft" size={14} />{prevPeriod ? periodDisplayLabel(prevPeriod, language) : d.previous}
                </button>
                <button type="button" className="adm-btn adm-btn--sm" disabled={!nextPeriod} onClick={onNext} aria-label={d.next}>
                    {nextPeriod ? periodDisplayLabel(nextPeriod, language) : d.next}<UiIcon name="chevronRight" size={14} />
                </button>
            </div>
        </nav>

        <header className="cvi-history-detail-head">
            <div>
                <h1 className="adm-title">{periodDisplayLabel(period, language)}</h1>
                <p className="adm-desc">{[range, state === "final" ? c.statusHelp.final : c.statusHelp.provisional].filter(Boolean).join(" · ")}</p>
            </div>
            <StateBadge state={state} c={c} />
        </header>

        <div className={`adm-callout ${state === "final" ? "is-neutral" : ""}`} role="note">
            <UiIcon name={state === "final" ? "shield" : "schedule"} size={15} />
            <div>{state === "final" ? d.readOnly : d.provisional}</div>
        </div>

        {loading ? <div className="cvi-history-card" aria-busy="true"><div className="cvi-history-skeleton"><span /><span /><span /></div><p className="cvi-history-muted">{d.loading}</p></div>
            : error ? <div className="adm-callout is-danger" role="alert"><div>{d.error}</div><button type="button" className="adm-btn adm-btn--sm" onClick={onRetry}>{d.retry}</button></div>
            : <>
                {people.length > 1 && <div className="cvi-history-card cvi-history-person-picker">
                    {roleOptions.length > 1 && <label className="adm-field"><span>{d.position}</span>
                        <select value={roleFilter} onChange={event => onRoleFilter(event.target.value)}>
                            <option value="all">{d.allRoles}</option>
                            {roleOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select></label>}
                    <label className="adm-field"><span>{d.person}</span>
                        <select value={subjectId || ""} onChange={event => onSubject(event.target.value)}>
                            {people.map(person => <option key={person.userId} value={person.userId}>{person.name}{person.positionName ? ` — ${person.positionName}` : ""}</option>)}
                        </select></label>
                </div>}

                {!selected ? <div className="cvi-history-card"><p className="cvi-history-section-empty">{d.noSnapshot}</p></div> : <>
                    <div className="cvi-history-card cvi-history-subject">
                        <div className="cvi-history-subject-name"><strong>{selected.name}</strong>{selected.positionName && <span>{selected.positionName}</span>}</div>
                        <ScoreTrio c={c} language={language} scores={trioScores} changes={changes} state={state}
                            emptyText={selected.scoreAvailable === false ? d.noSnapshot : undefined} />
                    </div>

                    <div className="cvi-history-sections">
                        <Section title={d.sections.routine} icon="routine" score={scores.routine ?? scores.responsibilities} language={language}>
                            <h4>{d.sections.routineManager}</h4>
                            {routine.length === 0 ? <Empty>{d.noneRecorded}</Empty> : <table className="cvi-history-table">
                                <thead><tr><th scope="col">{d.criterion}</th><th scope="col">{d.rating}</th></tr></thead>
                                <tbody>{routine.map((r, i) => <tr key={r.questionId || r.id || i}>
                                    <td>{r.text || r.question?.text || "—"}{(r.month || r.question?.month) && <small>{d.month(r.month || r.question?.month)}</small>}</td>
                                    <td className="is-nowrap">{levelText(r.levelName || r.level?.name) || formatPercent(r.normalizedScore, language)}</td>
                                </tr>)}</tbody>
                            </table>}
                            {self.length > 0 && <>
                                <h4>{d.sections.routineSelf}</h4>
                                <table className="cvi-history-table"><tbody>{self.map((r, i) => <tr key={r.id || i}>
                                    <td>{r.question?.text || r.questionText || "—"}{r.comment && <small>{r.comment}</small>}</td>
                                    <td className="is-nowrap">{levelText(r.level?.name || r.levelName) || "—"}</td>
                                </tr>)}</tbody></table>
                            </>}
                        </Section>

                        <Section title={d.sections.activities} icon="activity" score={scores.nonRoutine} language={language}>
                            {activities.length === 0 ? <Empty>{d.noneRecorded}</Empty> : <ul className="cvi-history-records">{activities.map(a => <li key={a.id}>
                                <div className="cvi-history-record-head">
                                    <strong>{a.title || d.untitled}</strong>
                                    <span className={`cvi-history-pill is-${String(a.status || "").toLowerCase()}`}>{statusText(a.status)}</span>
                                </div>
                                {a.description && <p>{a.description}</p>}
                                <small>{[a.activityGroup?.name, a.impactLevel?.name, toNumber(a.score) != null ? d.points(formatScore(a.score, language, 0)) : null].filter(Boolean).join(" · ")}</small>
                            </li>)}</ul>}
                        </Section>

                        <Section title={d.sections.goals} icon="target" score={scores.individualGoals} language={language}>
                            {goals.length === 0 ? <Empty>{d.noneRecorded}</Empty> : <ul className="cvi-history-records">{goals.map(g => <li key={g.id}>
                                <div className="cvi-history-record-head">
                                    <strong>{g.title}</strong>
                                    <span className={`cvi-history-pill is-${String(g.status || "").toLowerCase()}`}>{statusText(g.status)}</span>
                                </div>
                                {g.description && <p>{g.description}</p>}
                                <dl className="cvi-history-facts">
                                    <div><dt>{d.weight}</dt><dd>{formatPercent(g.weight, language)}</dd></div>
                                    <div><dt>{d.target}</dt><dd>{withUnit(g.target, g.unit, language)}</dd></div>
                                    <div><dt>{d.actual}</dt><dd>{withUnit(g.actual, g.unit, language)}</dd></div>
                                    <div><dt>{d.achievement}</dt><dd>{formatPercent(g.progress, language)}</dd></div>
                                </dl>
                                {toNumber(g.progress) != null && <div className="cvi-history-bar" aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, g.progress))}%` }} /></div>}
                                <small>{d.managerScore}: {g.managerScore == null ? "—" : `${formatScore(g.managerScore, language, 0)} / 100`}</small>
                                {g.evidenceNote && <small>{d.evidence}: {g.evidenceNote}</small>}
                                {g.managerNote && <small>{d.managerComment}: {g.managerNote}</small>}
                            </li>)}</ul>}
                        </Section>

                        <Section title={d.sections.peer} icon="peer" score={scores.peerFeedback} language={language}>
                            {peer?.enabled === false ? <Empty>{d.peerDisabled}</Empty>
                                : !peer ? <Empty>{hasBreakdown ? d.noneRecorded : d.notInSnapshot}</Empty>
                                : <>
                                    <p className="cvi-history-muted">{d.responses(Number(peer.responseCount) || 0)}</p>
                                    {(peer.questions || []).length > 0 && <table className="cvi-history-table"><tbody>{peer.questions.map(q => <tr key={q.questionId}>
                                        <td>{q.text}</td><td className="is-num">{formatPercent(q.normalizedScore, language)}</td>
                                    </tr>)}</tbody></table>}
                                </>}
                        </Section>

                        <Section title={d.sections.collective} icon="team" score={scores.organizational} language={language} wide>
                            {!org.levels ? <Empty>{hasBreakdown ? d.noneRecorded : d.notInSnapshot}</Empty> : ["team", "department", "organization"].map(level => {
                                const x = org.levels?.[level];
                                if (!x) return null;
                                return <div key={level} className="cvi-history-level">
                                    <h4>{d.levels[level]} <span>{d.levelSummary(formatPercent(x.weight, language), formatPercent(x.score, language))}</span></h4>
                                    {(x.indicators || []).length > 0 && <table className="cvi-history-table">
                                        <thead><tr><th scope="col">{d.indicator}</th><th scope="col" className="is-num">{d.target}</th><th scope="col" className="is-num">{d.actual}</th><th scope="col" className="is-num">{d.achievement}</th></tr></thead>
                                        <tbody>{x.indicators.map(ind => <tr key={ind.indicatorId}>
                                            <td>{ind.name}</td>
                                            <td className="is-num">{formatScore(ind.target, language)}</td>
                                            <td className="is-num">{formatScore(ind.actual, language)}</td>
                                            <td className="is-num">{formatPercent(ind.achievement, language)}</td>
                                        </tr>)}</tbody>
                                    </table>}
                                </div>;
                            })}
                        </Section>

                        <Section title={d.sections.calculation} icon="shield" language={language} wide>
                            <dl className="cvi-history-facts is-inline">
                                <div><dt>{d.formula}</dt><dd>{formulaVersion || "—"}</dd></div>
                                <div><dt>{d.source}</dt><dd>{selected.snapshot ? d.sourceFinal(selected.snapshot.version) : state === "final" ? "—" : d.sourceLive}</dd></div>
                                {selected.snapshot?.calculatedAt && <div><dt>{d.calculatedAt}</dt><dd>{fmtDate(selected.snapshot.calculatedAt)}</dd></div>}
                            </dl>
                        </Section>
                    </div>
                </>}
            </>}
    </div>;
}
